import { useMemo, useRef, useState } from 'react'
import { RippleState } from '../../components/shared/buttons/types'

const useRipple = (container: any, displayFocusRipple?: boolean) => {
  const [ripples, setRipples] = useState<RippleState[]>([])
  const [focusRipple, setFocusRipple] = useState<RippleState | undefined>(undefined)
  const nextKey = useRef(0)

  const handlers = useMemo(
    () => ({
      onTapStart: (event: MouseEvent | TouchEvent | PointerEvent) => {
        // ported from https://github.com/mui-org/material-ui/blob/4fba0dafd30f608937efa32883d151ba01fc9681/packages/material-ui/src/ButtonBase/TouchRipple.js#L164
        const element = container.current
        const rect = element
          ? element.getBoundingClientRect()
          : { width: 0, height: 0, left: 0, top: 0 }
        let clientX = 0
        let clientY = 0

        if (event as MouseEvent) {
          clientX = (event as MouseEvent).clientX
          clientY = (event as MouseEvent).clientY
        } else if (event as TouchEvent) {
          clientX = (event as TouchEvent).touches[0].clientX
          clientY = (event as TouchEvent).touches[0].clientY
        } else if (event as PointerEvent) {
          clientX = (event as PointerEvent).clientX
          clientY = (event as PointerEvent).clientY
        }

        let rippleX
        let rippleY
        if (clientX === 0 && clientY === 0) {
          rippleX = Math.round(rect.width / 2)
          rippleY = Math.round(rect.height / 2)
        } else {
          rippleX = Math.round(clientX - rect.left)
          rippleY = Math.round(clientY - rect.top)
        }

        const sizeX =
          Math.max(Math.abs((element ? element.clientWidth : 0) - rippleX), rippleX) * 2 + 2
        const sizeY =
          Math.max(Math.abs((element ? element.clientHeight : 0) - rippleY), rippleY) * 2 + 2
        const rippleSize = Math.sqrt(sizeX ** 2 + sizeY ** 2)

        setRipples([
          ...ripples,
          {
            key: nextKey.current.toString(),
            x: rippleX,
            y: rippleY,
            size: rippleSize
          }
        ])
        setFocusRipple(undefined)
        nextKey.current += 1
      },
      onTapEnd: () => {
        setRipples(ripples.slice(1))
      },
      onFocus: () => {
        if (!ripples.length && displayFocusRipple) {
          const element = container.current
          const rect = element
            ? element.getBoundingClientRect()
            : { width: 0, height: 0, left: 0, top: 0 }
          const rippleX = Math.round(rect.width / 2)
          const rippleY = Math.round(rect.height / 2)

          let rippleSize = Math.sqrt((2 * rect.width ** 2 + rect.height ** 2) / 3)

          // For some reason the animation is broken on Mobile Chrome if the size if even.
          if (rippleSize % 2 === 0) {
            rippleSize += 1
          }

          setFocusRipple({
            key: 'focus',
            x: rippleX,
            y: rippleY,
            size: rippleSize,
            pulsate: true
          })
        }
      },
      onBlur: () => setFocusRipple(undefined)
    }),
    [container, displayFocusRipple, ripples]
  )
  return [ripples, focusRipple, handlers] as const
}

export default useRipple
